import React, { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'

import { AppDispatch, GlobalState } from '../../../store'
import {
  disableOutputs,
  DraftActions,
  draftOutputs,
  DraftSources,
  removeOutputs,
} from '../../../redux/actions/outputsActions'
import { pluralize } from '../../../utils'

interface CommonActionsProps {
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
}

const DeleteDialog = ({ setSelected }: CommonActionsProps) => {
  const [confirmTextValue, setConfirmTextValue] = useState('')

  const {
    draft: { outputs, action, source },
  } = useSelector(({ outputsReducer }: GlobalState) => ({ draft: outputsReducer.draft }), shallowEqual)
  const dispatch = useDispatch<AppDispatch>()

  if (!outputs.length) return null

  const onCancelClick = () => {
    setConfirmTextValue('')

    dispatch(draftOutputs({ outputs: [] }))
  }
  const handleOk = () => {
    setConfirmTextValue('')

    dispatch(draftOutputs({ outputs: [] }))
    if (action === DraftActions.delete) {
      dispatch(removeOutputs(outputs.map(({ id }) => id)))
    }
    if (action === DraftActions.disable) {
      dispatch(disableOutputs(outputs.map(({ id }) => id)))
      setSelected([])
    }
  }
  const confirmText = (outputs.length === 1 ? outputs[0].name : `${action} outputs`).toLowerCase()
  const isConfirmRequired = source === DraftSources.batch && action === DraftActions.delete

  return (
    <Dialog
      open={outputs.length > 0 && (action === DraftActions.delete || action === DraftActions.disable)}
      disableRestoreFocus
      onClose={(_event, reason) => {
        if (reason === 'escapeKeyDown') {
          onCancelClick()
        }
      }}
      data-test-id="confirm-dialog"
    >
      <DialogContent>
        <DialogContentText gutterBottom component="div">
          {`Are you sure you want to ${action} ${
            outputs.length > 1 ? pluralize(outputs.length, 'output') : outputs[0].name
          }?`}
          {outputs.length > 1 && (
            <ul>
              {outputs.map((item) => (
                <li key={item.id}>{item.name}</li>
              ))}
            </ul>
          )}
        </DialogContentText>
        {isConfirmRequired && (
          <>
            <DialogContentText gutterBottom>
              To confirm, type{' '}
              <span style={{ fontStyle: 'italic', fontWeight: 'bold' }} data-test-id="confirm-text-value">
                {confirmText}
              </span>{' '}
              below
            </DialogContentText>
            <TextField
              data-test-id="confirm-text-field"
              autoFocus
              fullWidth
              placeholder={confirmText}
              value={confirmTextValue}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmTextValue.toLowerCase() === confirmText) {
                  handleOk()
                }
              }}
              onChange={(e) => setConfirmTextValue(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onCancelClick} data-test-id="cancel-btn">
          Cancel
        </Button>
        <Button
          variant="contained"
          color={action === DraftActions.delete ? 'error' : 'primary'}
          onClick={handleOk}
          data-test-id="ok-btn"
          disabled={isConfirmRequired ? confirmTextValue.toLowerCase() !== confirmText : false}
        >
          {action}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteDialog
